import { isResource } from '@medplum/core'
import type {
  Coding,
  List,
  Organization,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import type { SeenMedplumClientExtension } from '#src/client.ts'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  ICD10_SYSTEM,
  LIST_TYPE,
  SEEN_HEALTH_SYSTEM,
  SNOMED_CT_SYSTEM,
} from '#src/codeSystems.ts'
import { ExtendedList } from '#src/models/list.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

// How a SNOMED mapping stored in the org-conditions List was established.
// Written to the entry's `source` sub-extension, sharing the value space with
// the template-creation markers ('custom'/'imported'). seeded/scraped are
// low-confidence automatic sources and cannot overwrite an existing SNOMED
// mapping; learned/manual are higher-confidence and may replace any existing
// mapping.
export const CONDITION_MAPPING_SOURCES = [
  'seeded',
  'scraped',
  'learned',
  'manual',
] as const
export type ConditionMappingSource = (typeof CONDITION_MAPPING_SOURCES)[number]

export const CONDITION_TEMPLATE_SOURCES = [
  'custom',
  'imported',
  ...CONDITION_MAPPING_SOURCES,
] as const
export type ConditionTemplateSource =
  (typeof CONDITION_TEMPLATE_SOURCES)[number]

// A resolved, verified ICD-10→SNOMED mapping returned from the List.
export interface TrustedMapping {
  // The IMO-accepted SNOMED code Elation accepts for this ICD-10.
  snomed: Coding
  // How this entry entered the List. 'custom'/'imported' appear when a
  // clinician-created entry later gained a verified mapping.
  source?: ConditionTemplateSource
  // The ICD-10 Elation re-derived from snomed on the round-trip that verified
  // this mapping.
  verifiedRoundtripIcd10: string
}

// Parameters for upserting a verified SNOMED mapping into the List.
export interface UpsertMappingParams {
  icd10: string
  icd10Display?: string
  snomed: string
  snomedDisplay?: string
  verifiedRoundtripIcd10: string
  source: ConditionMappingSource
  verifiedAt: string
  // Entry title when creating a new entry. Defaults to icd10 when omitted.
  title?: string
  // Allow a seeded/scraped write to replace an existing SNOMED mapping. Set
  // only when the caller holds direct round-trip verification evidence for
  // the replacement (e.g. a live Elation sandbox check).
  overwrite?: boolean
}

export interface ConditionTemplate {
  title: string
  icd10Codes: Coding[]
  // snomedCodes[0] is the IMO-accepted SNOMED for this entry's ICD-10 when
  // verifiedRoundtripIcd10 is also set; otherwise it may be unverified or
  // absent.
  snomedCodes: Coding[]
  // The ICD-10 Elation re-derived from snomedCodes[0] on the round-trip that
  // established the mapping. Present only when the mapping is verified.
  verifiedRoundtripIcd10?: string | null
  // ISO datetime of the most recent round-trip verification.
  verifiedAt?: string | null
  addedOn: string
  addedBy: Reference<Practitioner | Organization>
  // How the entry entered the List ('custom'/'imported') or how its SNOMED
  // mapping was established (mapping sources).
  source?: ConditionTemplateSource
}

// A listed condition plus its position in List.entry. Titles are not unique
// within the list, so `index` is the stable identity used to remove exactly
// one row.
export interface OrganizationConditionEntry extends ConditionTemplate {
  index: number
}

export function normalizeConditionTemplateTitle(
  title: string | undefined
): string {
  return title?.trim().toLowerCase() ?? ''
}

// Dot-and-case-insensitive ICD-10 comparison key. The dot is purely cosmetic
// in ICD-10, and Elation/NLM sources are not consistent about including it.
export function normalizeIcd10Key(code: string): string {
  return code.replace(/\./g, '').trim().toUpperCase()
}

export class OrganizationConditionsList extends ExtendedList {
  static readonly listType = 'organization-conditions'

  static readonly conditionTemplateExtension = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/condition-template`

  static readonly code = codeableConcept({
    system: LIST_TYPE,
    code: OrganizationConditionsList.listType,
    display: 'Organization Conditions',
  })

  static isInstance(value: unknown): value is List {
    return (
      isResource(value) &&
      isOfType(value, 'List') &&
      hasCodeableConcept(value.code, OrganizationConditionsList.code)
    )
  }

  static byOrganization(organizationRef: Reference<Organization>): QueryTypes {
    return {
      source: organizationRef.reference,
      code: `${LIST_TYPE}|${OrganizationConditionsList.listType}`,
    }
  }

  constructor(
    data?: Omit<
      List,
      'resourceType' | 'status' | 'mode' | 'code' | 'source'
    > & {
      source: Reference<Organization>
    }
  ) {
    super({
      ...data,
      source: data?.source as List['source'],
      code: OrganizationConditionsList.code,
    })
  }

  add(template: ConditionTemplate): void {
    const entry = this.buildEntry(template)
    if (!entry) {
      return
    }

    const existingEntry = this.findEntryByTitle(
      normalizeConditionTemplateTitle(template.title)
    )
    if (existingEntry) {
      return
    }

    this.resource.entry = [...(this.resource.entry ?? []), entry]
  }

  // Upsert a verified ICD-10→SNOMED mapping onto the first entry that contains
  // icd10, creating a new single-code entry if none exists.
  //
  // Trust ordering: a seeded/scraped source cannot overwrite an entry that
  // already has any SNOMED code. A learned/manual source always writes.
  //
  // Returns 'created' when a new entry was added, 'updated' when an existing
  // entry's mapping was replaced, or 'skipped' when trust ordering blocked the
  // write.
  upsertMapping(
    params: UpsertMappingParams & { addedBy: Reference<Organization> }
  ): 'created' | 'updated' | 'skipped' {
    const targetKey = normalizeIcd10Key(params.icd10)
    const entry = this.resource.entry?.find((e) => {
      const t = this.extractTemplate(e)
      return t?.icd10Codes.some(
        (c) => normalizeIcd10Key(c.code ?? '') === targetKey
      )
    })

    if (!entry) {
      // No existing entry — add a new single-code entry. Bypass title-dedup
      // since we're keying on ICD-10, not title.
      const title = (params.title ?? params.icd10).trim() || params.icd10
      this.resource.entry = [
        ...(this.resource.entry ?? []),
        {
          item: { display: title },
          extension: [
            {
              url: OrganizationConditionsList.conditionTemplateExtension,
              extension: [
                { url: 'title', valueString: title },
                {
                  url: 'icd10',
                  valueCoding: {
                    system: ICD10_SYSTEM,
                    code: params.icd10,
                    display: params.icd10Display,
                  },
                },
                {
                  url: 'snomed',
                  valueCoding: {
                    system: SNOMED_CT_SYSTEM,
                    code: params.snomed,
                    display: params.snomedDisplay,
                  },
                },
                { url: 'addedOn', valueDateTime: params.verifiedAt },
                { url: 'addedBy', valueReference: params.addedBy },
                { url: 'source', valueString: params.source },
                {
                  url: 'verifiedRoundtripIcd10',
                  valueString: params.verifiedRoundtripIcd10,
                },
                { url: 'verifiedAt', valueDateTime: params.verifiedAt },
              ],
            },
          ],
        },
      ]
      return 'created'
    }

    // Check trust ordering against the existing mapping.
    const existing = this.extractTemplate(entry)
    const existingSnomed = existing?.snomedCodes[0]
    if (
      existingSnomed?.code &&
      !params.overwrite &&
      (params.source === 'seeded' || params.source === 'scraped')
    ) {
      return 'skipped'
    }

    // Update the mapping extensions in place.
    const templateExt = entry.extension?.find(
      (ext) => ext.url === OrganizationConditionsList.conditionTemplateExtension
    )
    if (!templateExt?.extension) {
      return 'skipped'
    }

    // 'custom'/'imported' mark how the entry entered the List and are
    // preserved; a stale mapping source is replaced along with the mapping.
    // 'provenance' is a retired sub-extension cleaned up on rewrite.
    const existingSource = templateExt.extension.find(
      (e) => e.url === 'source'
    )?.valueString
    const preserveSource =
      existingSource === 'custom' || existingSource === 'imported'
    const mappingUrls = new Set([
      'snomed',
      'verifiedRoundtripIcd10',
      'verifiedAt',
      'provenance',
      ...(preserveSource ? [] : ['source']),
    ])
    templateExt.extension = [
      ...templateExt.extension.filter((e) => !mappingUrls.has(e.url ?? '')),
      {
        url: 'snomed',
        valueCoding: {
          system: SNOMED_CT_SYSTEM,
          code: params.snomed,
          display: params.snomedDisplay,
        },
      },
      ...(preserveSource
        ? []
        : [{ url: 'source', valueString: params.source }]),
      {
        url: 'verifiedRoundtripIcd10',
        valueString: params.verifiedRoundtripIcd10,
      },
      { url: 'verifiedAt', valueDateTime: params.verifiedAt },
    ]
    return 'updated'
  }

  // Record round-trip verification evidence on the entry holding icd10
  // WITHOUT changing its mapping or source. Returns 'stamped' when the
  // entry's current SNOMED matches the verified one, 'no-match' otherwise
  // (entry missing, no SNOMED, or a different SNOMED — callers should
  // upsertMapping instead).
  stampVerification(params: {
    icd10: string
    snomed: string
    verifiedRoundtripIcd10: string
    verifiedAt: string
  }): 'stamped' | 'no-match' {
    const targetKey = normalizeIcd10Key(params.icd10)
    const entry = this.resource.entry?.find((e) => {
      const t = this.extractTemplate(e)
      return t?.icd10Codes.some(
        (c) => normalizeIcd10Key(c.code ?? '') === targetKey
      )
    })
    if (!entry) {
      return 'no-match'
    }
    if (this.extractTemplate(entry)?.snomedCodes[0]?.code !== params.snomed) {
      return 'no-match'
    }
    const templateExt = entry.extension?.find(
      (ext) => ext.url === OrganizationConditionsList.conditionTemplateExtension
    )
    if (!templateExt?.extension) {
      return 'no-match'
    }
    // 'provenance' is a retired sub-extension cleaned up on rewrite.
    const stampUrls = new Set([
      'verifiedRoundtripIcd10',
      'verifiedAt',
      'provenance',
    ])
    templateExt.extension = [
      ...templateExt.extension.filter((e) => !stampUrls.has(e.url ?? '')),
      {
        url: 'verifiedRoundtripIcd10',
        valueString: params.verifiedRoundtripIcd10,
      },
      { url: 'verifiedAt', valueDateTime: params.verifiedAt },
    ]
    return 'stamped'
  }

  remove(icd10Code: string): void {
    let removed = false
    this.resource.entry = (this.resource.entry ?? []).filter((entry) => {
      if (removed) {
        return true
      }
      const template = this.extractTemplate(entry)
      const shouldRemove =
        template?.icd10Codes.some((coding) => coding.code === icd10Code) ??
        false
      if (shouldRemove) {
        removed = true
        return false
      }
      return true
    })
  }

  // Hard-delete a single entry by its position in List.entry. Titles are not
  // unique within the list, so the index (from the `conditions` getter) is the
  // stable identity for the row. Prior state remains recoverable via the List
  // resource's Medplum version history.
  removeByIndex(index: number): void {
    const entries = this.resource.entry
    if (!entries || index < 0 || index >= entries.length) {
      return
    }
    this.resource.entry = entries.filter((_, i) => i !== index)
  }

  // Replace the entry at `index` (from the `conditions` getter) in place,
  // keeping its position. Unlike add(), no title-dedup guard applies: titles
  // are not unique in the list, and an edit must never silently drop the
  // entry it targets.
  replaceByIndex(index: number, template: ConditionTemplate): void {
    const entries = this.resource.entry
    if (!entries || index < 0 || index >= entries.length) {
      return
    }
    const entry = this.buildEntry(template)
    if (!entry) {
      return
    }
    this.resource.entry = entries.map((existing, i) =>
      i === index ? entry : existing
    )
  }

  // Resolve a trusted ICD-10→SNOMED mapping from the org-conditions List for
  // a given organization. Returns null when the List does not exist or no
  // verified mapping is found for the ICD-10.
  static async resolve(
    client: SeenMedplumClientExtension,
    orgRef: Reference<Organization>,
    icd10: string
  ): Promise<TrustedMapping | null> {
    const list = await client.searchOneResourceClass(
      OrganizationConditionsList,
      OrganizationConditionsList.byOrganization(orgRef)
    )
    if (!list) {
      return null
    }

    const targetKey = normalizeIcd10Key(icd10)
    for (const template of list.conditions) {
      if (
        !template.icd10Codes.some(
          (c) => normalizeIcd10Key(c.code ?? '') === targetKey
        )
      ) {
        continue
      }
      const snomed = template.snomedCodes[0]
      if (!snomed?.code) {
        continue
      }
      // Trust requires round-trip verification evidence; the source is
      // informational (a 'custom' entry whose mapping was later verified is
      // still trusted).
      if (!template.verifiedRoundtripIcd10) {
        continue
      }
      return {
        snomed,
        source: template.source,
        verifiedRoundtripIcd10: template.verifiedRoundtripIcd10,
      }
    }
    return null
  }

  // Upsert a verified ICD-10→SNOMED mapping into the org's List, creating the
  // List if it does not yet exist. Applies trust ordering (see upsertMapping).
  // Returns 'created', 'updated', or 'skipped'.
  static async upsert(
    client: SeenMedplumClientExtension,
    orgRef: Reference<Organization>,
    params: UpsertMappingParams
  ): Promise<'created' | 'updated' | 'skipped'> {
    const outcomes = await OrganizationConditionsList.upsertMany(
      client,
      orgRef,
      [params]
    )
    return outcomes[0] ?? 'skipped'
  }

  // Upsert several verified mappings in a single read-modify-write cycle
  // against the org's List, creating it if it does not yet exist. Trust
  // ordering applies per mapping (see upsertMapping). Returns one outcome per
  // input, in order; the List is written at most once, and only when a
  // mapping was applied.
  static async upsertMany(
    client: Pick<
      SeenMedplumClientExtension,
      'searchOneResourceClass' | 'upsertResourceClass'
    >,
    orgRef: Reference<Organization>,
    paramsList: UpsertMappingParams[]
  ): Promise<('created' | 'updated' | 'skipped')[]> {
    const list =
      (await client.searchOneResourceClass(
        OrganizationConditionsList,
        OrganizationConditionsList.byOrganization(orgRef)
      )) ?? new OrganizationConditionsList({ source: orgRef })

    const outcomes = paramsList.map((params) =>
      list.upsertMapping({ ...params, addedBy: orgRef })
    )
    if (outcomes.some((outcome) => outcome !== 'skipped')) {
      await client.upsertResourceClass(
        list,
        OrganizationConditionsList.byOrganization(orgRef)
      )
    }
    return outcomes
  }

  // Build a List entry from a template, or undefined when the template lacks
  // a title or any ICD-10 code.
  private buildEntry(
    template: ConditionTemplate
  ): NonNullable<List['entry']>[number] | undefined {
    const title = template.title.trim()
    if (!title) {
      return undefined
    }

    const icd10Codes = template.icd10Codes
      .filter((coding) => !!coding.code)
      .map((coding) => ({
        system: coding.system ?? ICD10_SYSTEM,
        code: coding.code as string,
        display: coding.display,
      }))
    if (icd10Codes.length === 0) {
      return undefined
    }

    const snomedCodes = template.snomedCodes
      .filter((coding) => !!coding.code)
      .map((coding) => ({
        system: coding.system ?? SNOMED_CT_SYSTEM,
        code: coding.code as string,
        display: coding.display,
      }))

    return {
      item: { display: title },
      extension: [
        {
          url: OrganizationConditionsList.conditionTemplateExtension,
          extension: [
            { url: 'title', valueString: title },
            ...icd10Codes.map((coding) => ({
              url: 'icd10',
              valueCoding: coding,
            })),
            ...snomedCodes.map((coding) => ({
              url: 'snomed',
              valueCoding: coding,
            })),
            { url: 'addedOn', valueDateTime: template.addedOn },
            { url: 'addedBy', valueReference: template.addedBy },
            ...(template.source
              ? [{ url: 'source', valueString: template.source }]
              : []),
            ...(template.verifiedRoundtripIcd10
              ? [
                  {
                    url: 'verifiedRoundtripIcd10',
                    valueString: template.verifiedRoundtripIcd10,
                  },
                ]
              : []),
            ...(template.verifiedAt
              ? [{ url: 'verifiedAt', valueDateTime: template.verifiedAt }]
              : []),
          ],
        },
      ],
    }
  }

  private findEntryByTitle(
    normalizedTitle: string
  ): NonNullable<List['entry']>[number] | undefined {
    if (!normalizedTitle) {
      return undefined
    }
    return this.resource.entry?.find((entry) => {
      const template = this.extractTemplate(entry)
      return (
        normalizeConditionTemplateTitle(template?.title) === normalizedTitle
      )
    })
  }

  private extractTemplate(
    entry: NonNullable<List['entry']>[number]
  ): ConditionTemplate | undefined {
    const templateExt = entry.extension?.find(
      (ext) => ext.url === OrganizationConditionsList.conditionTemplateExtension
    )
    if (!templateExt?.extension) {
      return undefined
    }

    const titleExt = templateExt.extension.find((e) => e.url === 'title')
    const addedOnExt = templateExt.extension.find((e) => e.url === 'addedOn')
    const addedByExt = templateExt.extension.find((e) => e.url === 'addedBy')
    const sourceExt = templateExt.extension.find((e) => e.url === 'source')
    const verifiedRoundtripIcd10Ext = templateExt.extension.find(
      (e) => e.url === 'verifiedRoundtripIcd10'
    )
    const verifiedAtExt = templateExt.extension.find(
      (e) => e.url === 'verifiedAt'
    )

    const title = titleExt?.valueString?.trim()
    const icd10Codes = templateExt.extension
      .filter((ext) => ext.url === 'icd10' && !!ext.valueCoding?.code)
      .map((ext) => ({
        system: ext.valueCoding?.system ?? ICD10_SYSTEM,
        code: ext.valueCoding?.code as string,
        display: ext.valueCoding?.display,
      }))
    const snomedCodes = templateExt.extension
      .filter((ext) => ext.url === 'snomed' && !!ext.valueCoding?.code)
      .map((ext) => ({
        system: ext.valueCoding?.system ?? SNOMED_CT_SYSTEM,
        code: ext.valueCoding?.code as string,
        display: ext.valueCoding?.display,
      }))
    const addedOn = addedOnExt?.valueDateTime
    const addedBy = addedByExt?.valueReference

    if (!title || icd10Codes.length === 0 || !addedOn || !addedBy) {
      return undefined
    }

    const rawSource = sourceExt?.valueString
    const source: ConditionTemplateSource | undefined =
      rawSource !== undefined &&
      (CONDITION_TEMPLATE_SOURCES as readonly string[]).includes(rawSource)
        ? (rawSource as ConditionTemplateSource)
        : undefined

    return {
      title,
      icd10Codes,
      snomedCodes,
      addedOn,
      addedBy: addedBy as Reference<Practitioner | Organization>,
      ...(source !== undefined && { source }),
      ...(verifiedRoundtripIcd10Ext?.valueString !== undefined && {
        verifiedRoundtripIcd10: verifiedRoundtripIcd10Ext.valueString,
      }),
      ...(verifiedAtExt?.valueDateTime !== undefined && {
        verifiedAt: verifiedAtExt.valueDateTime,
      }),
    }
  }

  // Each row carries its index in List.entry so callers can remove exactly that
  // entry; the index reflects the original entry position, not the post-filter
  // array position.
  get conditions(): OrganizationConditionEntry[] {
    return (
      this.resource.entry
        ?.map((entry, index) => {
          const template = this.extractTemplate(entry)
          return template ? { ...template, index } : undefined
        })
        .filter((entry): entry is OrganizationConditionEntry => !!entry) ?? []
    )
  }
}
